/**
 * ChatRunView - renders one AssistantRun turn.
 *
 * The run renders as a Claude Code-style activity chain (RunStepChain): every
 * step, tool call, question and text block is a chain item with a colored
 * status dot, linked by a vertical rail. The chain is always visible - it is
 * part of the conversation flow, never hidden behind a collapsible panel. The
 * live "what is happening right now" line is the RunStatusStrip above the
 * composer (mounted by AiChatPane).
 *
 * Also shows:
 * - Streamed text content (rendered as markdown via react-markdown)
 * - RAG references list (compact, collapsed by default)
 * - Confirmation card when lifecycleState === 'awaiting_confirmation'
 * - Error state with retry button
 */

import type { AiMessageActionHandler, UserPromptResponse } from '@protoss/shared';
import { isExecutionTerminal, isUserFacingStep } from '@protoss/shared';
import { Collapsible, Tooltip } from '@protoss/ui';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { useAiChatReferenceRef } from '../AiChatReferenceContext';
import { useProtectedImageUrl } from '../../../hooks/useProtectedImageUrl';
import { useWorkspaceAssetMeta } from '../../../hooks/useWorkspaceAssetMeta';
import { looksLikeInternalError } from '../../../lib/error';
import { deriveExecutionStatusFromChildEntries } from '../../../lib/executionStatusHelpers';
import { useTypewriter } from '../hooks/useTypewriter';
import type { AssistantRun, RunChildEntry, RunSegment } from '../state/chatState';
import { AiStructuredMessage } from './AiStructuredMessage';
import { ChainItem, ChainSummaryRow, ResultLine, StepChainRow } from './RunStepChain';

// ── Legacy child entry row (runs persisted before executionStatus existed) ──

function ChildEntryRow({ entry }: { entry: RunChildEntry }) {
  if (entry.kind === 'status') {
    return (
      <ChainItem dot="neutral">
        <span className="ai-chat-chain__label">{entry.label}</span>
      </ChainItem>
    );
  }

  // tool entry
  const dot =
    entry.status === 'failed' ? 'failed' : entry.status === 'running' ? 'running' : 'completed';

  return (
    <ChainItem dot={dot}>
      <span className="ai-chat-chain__label">{entry.label ?? entry.toolName}</span>
      {entry.status === 'running' && entry.progressText && (
        <span className="ai-chat-chain__result">{entry.progressText}</span>
      )}
      <ResultLine text={entry.resultSummary} />
    </ChainItem>
  );
}

// ── RAG reference list ────────────────────────────────────────────────────

function fileBasename(filePath: string): string {
  return filePath.split('/').pop() ?? filePath;
}

function RagReferenceList({ references }: { references: AssistantRun['ragReferences'] }) {
  // A host (e.g. the deal workspace) may register a click handler to open the source; when
  // absent (e.g. the repo copilot) the rows stay plain, non-clickable.
  const refHandlerRef = useAiChatReferenceRef();
  const cited = references.filter((r) => r.cited);
  const retrieved = references.filter((r) => !r.cited);
  const allRefs = [...cited, ...retrieved];

  if (allRefs.length === 0) return null;

  return (
    <details className="ai-chat-run__references">
      <summary className="ai-chat-run__references-summary">
        {cited.length} source{cited.length !== 1 ? 's' : ''}
      </summary>
      <div className="ai-chat-run__references-list">
        {allRefs.map((ref) => {
          const inner = (
            <>
              <span className="ai-chat-run__reference-index">[{ref.index}]</span>
              <Tooltip.Simple label={ref.title}>
                <span
                  className={`ai-chat-run__reference-title${ref.sourceType === 'repo_file' ? ' ai-chat-run__reference-title--file' : ''}`}
                >
                  {ref.sourceType === 'repo_file' ? fileBasename(ref.title) : ref.title}
                </span>
              </Tooltip.Simple>
              {ref.sourceType === 'repo_file' && ref.title.includes('/') && (
                <span className="ai-chat-run__reference-path">{ref.title}</span>
              )}
              {ref.snippet && <span className="ai-chat-run__reference-snippet">{ref.snippet}</span>}
            </>
          );
          const dimClass = ref.cited ? '' : ' ai-chat-run__reference--dim';
          const onClick = refHandlerRef?.current ? () => refHandlerRef.current?.(ref) : undefined;
          return onClick ? (
            <button
              key={ref.referenceId}
              type="button"
              className={`ai-chat-run__reference ai-chat-run__reference--clickable${dimClass}`}
              onClick={onClick}
            >
              {inner}
            </button>
          ) : (
            <div key={ref.referenceId} className={`ai-chat-run__reference${dimClass}`}>
              {inner}
            </div>
          );
        })}
      </div>
    </details>
  );
}

// ── Confirmation card ─────────────────────────────────────────────────────

interface ConfirmationCardProps {
  prompt: string;
  details?: string;
  onConfirm: () => void;
  onDeny: () => void;
}

function ConfirmationCard({ prompt, details, onConfirm, onDeny }: ConfirmationCardProps) {
  return (
    <div className="ai-chat-confirmation" role="alertdialog" aria-label="Confirmation required">
      <p className="ai-chat-confirmation__prompt">{prompt}</p>
      {details && <p className="ai-chat-confirmation__details">{details}</p>}
      <div className="ai-chat-confirmation__actions">
        <button type="button" className="ai-chat-btn ai-chat-btn--confirm" onClick={onConfirm}>
          Confirm
        </button>
        <button type="button" className="ai-chat-btn ai-chat-btn--deny" onClick={onDeny}>
          Deny
        </button>
      </div>
    </div>
  );
}

// ── Generalised mid-run prompt marker ───────────────────────────────────────
// The INTERACTIVE rendering lives in the UserInteractionDrawer above the
// composer (the single ask_user renderer - CLAUDE.md "Reusable primitives").
// The transcript only marks that the run is waiting, as a chain item.

function PendingPromptRow({ prompt }: { prompt: NonNullable<AssistantRun['pendingPrompt']> }) {
  return (
    <ChainItem dot="waiting">
      <span className="ai-chat-chain__label">{prompt.prompt}</span>
      {prompt.details && <span className="ai-chat-chain__detail">{prompt.details}</span>}
      <span className="ai-chat-chain__meta" role="status">
        Waiting for your input - answer in the panel below.
      </span>
    </ChainItem>
  );
}

// ── Conversational segment rendering ───────────────────────────────────────
//
// [Conversational copilot] When a run carries ordered segments, the turn renders
// as an interleaved transcript - text → tool → question → answer → text - each
// as a chain item, so the whole run reads as one continuous activity chain.
// Legacy/history runs (no segments) keep the steps + text path.

function ToolSegmentRow({ segment }: { segment: Extract<RunSegment, { kind: 'tool' }> }) {
  const dot =
    segment.status === 'failed' ? 'failed' : segment.status === 'running' ? 'running' : 'completed';
  return (
    <ChainItem dot={dot}>
      <span className="ai-chat-chain__label">{segment.label ?? segment.toolName}</span>
      <ResultLine text={segment.resultSummary} />
    </ChainItem>
  );
}

/**
 * One uploaded file in an answered prompt, rendered as a list item. Switches on
 * the asset's mime type: images render as a thumbnail, anything else (e.g.
 * .docx/.pdf) renders by name - never a broken <img>. The image URL is only built
 * for images, so no blob is fetched for documents.
 */
function AnsweredFileItem({ assetUuid }: { assetUuid: string }) {
  const meta = useWorkspaceAssetMeta(assetUuid);
  const isImage = meta?.mimeType?.startsWith('image/') ?? false;
  const thumbUrl = useProtectedImageUrl(
    isImage ? `/api/workspace-assets/${assetUuid}/content` : null
  );
  if (isImage && thumbUrl) {
    return (
      <li className="ai-chat-run__prompt-file ai-chat-run__prompt-file--image">
        <img
          className="ai-chat-run__prompt-thumb"
          src={thumbUrl}
          alt={meta?.originalFileName ?? 'Uploaded image'}
        />
      </li>
    );
  }
  return <li className="ai-chat-run__prompt-file">{meta?.originalFileName ?? 'Uploaded file'}</li>;
}

function AnsweredPromptRow({
  segment,
  onChangeAnswer,
}: {
  segment: Extract<RunSegment, { kind: 'prompt' }>;
  /** [HITL navigation] When set, renders a "Change answer" link that asks the skill to redo this question. */
  onChangeAnswer?: () => void;
}) {
  const assetUuids =
    segment.answerAssetUuids ?? (segment.answerAssetUuid ? [segment.answerAssetUuid] : []);
  const hasFiles = assetUuids.length > 0;
  // The question, the answer, and the "Change answer" link each render on their
  // own line, stacked and left-aligned by the chain body's flex column.
  const changeAnswerButton = onChangeAnswer ? (
    <button type="button" className="ai-chat-run__prompt-change" onClick={onChangeAnswer}>
      Change answer
    </button>
  ) : null;
  const hasFormAnswers = (segment.formAnswers?.length ?? 0) > 0;
  return (
    <ChainItem dot="completed">
      <span className="ai-chat-chain__label">{segment.prompt}</span>
      {/* Form (multi-tab) prompts: show every section's choice, not one flattened
          string - "Client Background → AI Generated", one row per section. */}
      {hasFormAnswers ? (
        <ul className="ai-chat-run__prompt-form-answers">
          {segment.formAnswers!.map((fa) => (
            <li key={fa.questionId} className="ai-chat-run__prompt-form-answer">
              <span className="ai-chat-run__prompt-form-label">{fa.label}</span>
              <span className="ai-chat-run__prompt-form-value">{fa.value}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {/* The file list below is the representation for file answers, so the
          generic "- Uploaded file" answer line is suppressed when files are present. */}
      {!hasFormAnswers && segment.answer && !hasFiles ? (
        <span className="ai-chat-chain__answer">{segment.answer}</span>
      ) : null}
      {hasFiles ? (
        <ul className="ai-chat-run__prompt-files">
          {assetUuids.map((uuid) => (
            <AnsweredFileItem key={uuid} assetUuid={uuid} />
          ))}
        </ul>
      ) : null}
      {/* Selected option(s) that carried a preview (e.g. the base questionnaire):
          shown collapsed so the chat isn't flooded - expand to inspect the record. */}
      {(segment.answerPreviews?.length ?? 0) > 0 ? (
        <div className="ai-chat-run__prompt-previews">
          {segment.answerPreviews!.map((p, i) => (
            <Collapsible.Root key={`${p.label}-${i}`} className="ai-chat-run__prompt-preview">
              <Collapsible.Trigger className="ai-chat-run__prompt-preview-trigger">
                <span aria-hidden="true" className="ai-chat-run__prompt-preview-chevron">
                  ▸
                </span>
                {p.label}
              </Collapsible.Trigger>
              <Collapsible.Content className="ai-chat-run__prompt-preview-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.preview}</ReactMarkdown>
              </Collapsible.Content>
            </Collapsible.Root>
          ))}
        </div>
      ) : null}
      {changeAnswerButton}
    </ChainItem>
  );
}

// ── Citation rendering ───────────────────────────────────────────────────

const CITATION_RE = /(\[\d+(?:,\s*\d+)*\])/g;

/**
 * Walks React children and replaces [N] / [N, M] text patterns with styled <sup> elements.
 * Brackets are preserved in the superscript: [1] → <sup>[1]</sup>, [3, 4] → <sup>[3, 4]</sup>.
 * When `onCite` is provided (a host registered a reference-click handler), each number inside a
 * citation becomes a clickable link that opens its source; otherwise it stays a plain marker.
 * Handles mixed children (strings + React elements) from react-markdown.
 */
function renderWithCitations(
  children: React.ReactNode,
  onCite?: (index: number) => void
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child !== 'string') return child;
    const parts = child.split(CITATION_RE);
    if (parts.length === 1) return child;
    return parts.map((part, i) => {
      if (i % 2 === 0) return part; // even indices are the plain text between citations
      // A citation token like "[1]" or "[1, 2, 3]". Without a handler it's a plain marker.
      if (!onCite) {
        return (
          <sup key={i} className="ai-chat-run__citation">
            {part}
          </sup>
        );
      }
      const nums = part
        .slice(1, -1)
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      return (
        <sup key={i} className="ai-chat-run__citation">
          [
          {nums.map((n, j) => (
            <React.Fragment key={n}>
              {j > 0 ? ', ' : ''}
              <button
                type="button"
                className="ai-chat-run__citation-link"
                onClick={() => onCite(Number(n))}
              >
                {n}
              </button>
            </React.Fragment>
          ))}
          ]
        </sup>
      );
    });
  });
}

function MarkdownText({
  text,
  references,
}: {
  text: string;
  references?: AssistantRun['ragReferences'];
}) {
  // When a host registered a reference-click handler, map each [N] to its source so the inline
  // citation opens the reference dialog (referenceId carries the document uuid).
  const refHandlerRef = useAiChatReferenceRef();
  const onCite = React.useMemo(() => {
    if (!refHandlerRef?.current || !references || references.length === 0) return undefined;
    const byIndex = new Map(references.map((r) => [r.index, r] as const));
    return (index: number) => {
      const ref = byIndex.get(index);
      if (ref) refHandlerRef.current?.(ref);
    };
  }, [refHandlerRef, references]);
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
        p: ({ children }) => <p>{renderWithCitations(children, onCite)}</p>,
        li: ({ children }) => <li>{renderWithCitations(children, onCite)}</li>,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

// ── Main export ───────────────────────────────────────────────────────────

export interface ChatRunViewProps {
  run: AssistantRun;
  onConfirm: () => void;
  onDeny: () => void;
  onRetry: () => void;
  onAction?: AiMessageActionHandler;
  /** Callback to persist the user's action result to the DB. */
  onActionSettled?: (
    runId: string,
    messageUuid: string,
    actionResult: 'accepted' | 'dismissed',
    acceptedFieldKeys: string[],
    cardKey?: string
  ) => void;
  /** False when a newer run exists. */
  isLatestRun?: boolean;
  /**
   * [HITL navigation] Responds to the run's pending prompt (same channel as the
   * UserInteractionDrawer) - used by "Change answer" to send a revise request.
   */
  onRespondPrompt?: (promptId: string, response: UserPromptResponse) => void;
}

export function ChatRunView({
  run,
  onConfirm,
  onDeny,
  onRetry,
  onAction,
  onActionSettled,
  isLatestRun: _isLatestRun,
  onRespondPrompt,
}: ChatRunViewProps) {
  const { workspace } = useWorkspace();
  const modelLabel = React.useMemo(() => {
    const match = workspace?.aiModels?.find((m) => m.id === run.model);
    return match?.label ?? run.model;
  }, [workspace?.aiModels, run.model]);

  const isActive =
    run.lifecycleState === 'streaming' ||
    run.lifecycleState === 'pending_start' ||
    run.lifecycleState === 'resuming' ||
    run.lifecycleState === 'finalizing';

  const displayedText = useTypewriter(run.text, run.streaming);

  const isError = run.lifecycleState === 'error';
  const isConfirm = run.lifecycleState === 'awaiting_confirmation';
  const hasSegments = (run.segments?.length ?? 0) > 0;

  // Derive executionStatus from childEntries if not present (backward compatibility)
  const executionStatus = React.useMemo(() => {
    if (run.executionStatus) {
      return run.executionStatus;
    }
    // Fallback: derive from legacy childEntries
    return deriveExecutionStatusFromChildEntries(
      run.childEntries,
      run.lifecycleState,
      run.startedAt,
      run.endedAt
    );
  }, [run.executionStatus, run.childEntries, run.lifecycleState, run.startedAt, run.endedAt]);

  // Steps rendered in the chain: internal pipeline steps stay hidden; in
  // segments mode top-level tool executions already render inline as tool
  // segments, so the step copies are dropped to avoid double rows.
  const chainSteps = React.useMemo(() => {
    const visible = executionStatus.steps.filter((step) => isUserFacingStep(step.id));
    return hasSegments ? visible.filter((step) => step.type !== 'tool_execution') : visible;
  }, [executionStatus.steps, hasSegments]);

  const isTerminal = isExecutionTerminal(executionStatus.phase);
  const hasChainContent =
    chainSteps.length > 0 || hasSegments || isActive || executionStatus.phase === 'cancelled';
  // `!isTerminal` keeps the placeholder from co-existing with the "Done …"
  // summary row: isActive keys off lifecycleState while isTerminal keys off
  // executionStatus.phase, and on any render where the two momentarily disagree
  // the placeholder would otherwise flash above the summary.
  const showPreparingRow =
    isActive && !isTerminal && chainSteps.length === 0 && !hasSegments && !run.text;

  // Render one conversational segment (text / tool / prompt) as a keyed chain node, or null when it
  // has nothing to show (empty text). Extracted so steps and segments can be INTERLEAVED by stream
  // order below (see chainItems) instead of rendered as two separate blocks.
  const renderSegment = (segment: RunSegment): React.ReactNode => {
    if (segment.kind === 'text') {
      if (!segment.text) return null;
      return (
        <ChainItem key={`text-${segment.messageId}`} dot="neutral">
          <div className="ai-chat-run__text" aria-live={run.streaming ? 'polite' : undefined}>
            <MarkdownText text={segment.text} references={run.ragReferences} />
          </div>
        </ChainItem>
      );
    }
    if (segment.kind === 'tool') {
      return <ToolSegmentRow key={`tool-${segment.toolCallId}`} segment={segment} />;
    }
    // Prompt segment: a live question shows a waiting marker (the interactive control is the
    // UserInteractionDrawer above the composer); answered (or stale) questions render as a muted Q-A
    // record.
    const isLivePrompt =
      isConfirm &&
      run.pendingPrompt?.promptId === segment.promptId &&
      segment.answer === undefined;
    if (isLivePrompt) {
      return <PendingPromptRow key={`prompt-${segment.promptId}`} prompt={run.pendingPrompt!} />;
    }
    // "Change answer" rides the LIVE prompt's respond channel (a resolved prompt has no resolver) -
    // so it is only offered while another question is pending and the skill allows navigation.
    const pending = run.pendingPrompt;
    const canRevise =
      isConfirm &&
      onRespondPrompt !== undefined &&
      pending?.canGoBack === true &&
      segment.answer !== undefined &&
      segment.promptId !== pending.promptId;
    return (
      <AnsweredPromptRow
        key={`prompt-${segment.promptId}`}
        segment={segment}
        onChangeAnswer={
          canRevise
            ? () =>
                onRespondPrompt(pending.promptId, {
                  kind: pending.kind,
                  action: 'revise',
                  reviseTarget: { promptId: segment.promptId, prompt: segment.prompt },
                })
            : undefined
        }
      />
    );
  };

  // [render.narration_inline] ONE stream-ordered chain. Steps and text/tool/prompt segments are
  // merged and sorted by their `order` (the creating event's seq), so mid-run narration renders
  // INLINE at its emission point instead of being bolted below the whole step chain (the old
  // steps-then-segments layout). Items without `order` (legacy/history runs) fall back to
  // steps-before-segments via the index offsets, preserving prior behaviour.
  const SEGMENT_FALLBACK_BASE = Number.MAX_SAFE_INTEGER / 2;
  const chainItems: Array<{ order: number; node: React.ReactNode }> = [];
  chainSteps.forEach((step, i) => {
    chainItems.push({
      order: step.order ?? i,
      node: (
        <StepChainRow key={`step-${step.id}`} step={step} activeStepId={executionStatus.activeStepId} />
      ),
    });
  });
  if (hasSegments) {
    run.segments!.forEach((segment, i) => {
      const node = renderSegment(segment);
      if (node === null) return;
      chainItems.push({ order: segment.order ?? SEGMENT_FALLBACK_BASE + i, node });
    });
  }
  // Stable sort (V8/JSC Array.sort is stable) - equal orders keep insertion order.
  chainItems.sort((a, b) => a.order - b.order);

  return (
    <div className={`ai-chat-run ai-chat-run--${run.lifecycleState}`} data-run-id={run.runId}>
      {/* Activity chain - steps, tools, questions and text as one vertical flow */}
      {hasChainContent && (
        <div className="ai-chat-chain" role="list" aria-label="Run activity">
          {/* [render.narration_inline] Steps + text/tool/prompt segments interleaved by stream order
              (chainItems) - mid-run narration renders inline at its emission point, not bolted below
              the whole step chain. */}
          {chainItems.map((item) => item.node)}

          {showPreparingRow && (
            <ChainItem dot="running" className="ai-chat-chain__item--active">
              <span className="ai-chat-chain__label">Preparing…</span>
            </ChainItem>
          )}

          {/* Legacy child entries (runs without executionStatus or segments) */}
          {!run.executionStatus &&
            !hasSegments &&
            chainSteps.length === 0 &&
            run.childEntries.map((entry, i) => (
              <ChildEntryRow key={entry.kind === 'tool' ? entry.toolCallId : i} entry={entry} />
            ))}

          {/* Terminal summary closes the chain */}
          {isTerminal && <ChainSummaryRow status={executionStatus} modelLabel={modelLabel} />}
        </div>
      )}

      {/* Streamed text - answer body for non-segment runs. Live copilot runs render
          their prose inline as text segments (above); rehydrated history has no
          segments, so the persisted prose renders here - including alongside a
          structured card, where the card summarizes and the prose explains. (Empty
          content_text - e.g. pure generation-result cards - simply renders nothing.) */}
      {!hasSegments && run.text && (
        <div className="ai-chat-run__text" aria-live={run.streaming ? 'polite' : undefined}>
          <MarkdownText text={displayedText} references={run.ragReferences} />
        </div>
      )}

      {/* Structured payload card */}
      {run.structuredPayload && (
        <AiStructuredMessage
          payload={run.structuredPayload}
          messageId={run.messageId ?? run.runId}
          initialActionState={run.actionResult}
          initialCheckedFieldKeys={run.acceptedFieldKeys}
          initialCardStates={run.cardActionResults}
          onAction={onAction}
          onActionSettled={(actionResult, selectedFieldKeys, cardKey) => {
            if (run.messageId) {
              onActionSettled?.(run.runId, run.messageId, actionResult, selectedFieldKeys, cardKey);
            }
          }}
        />
      )}

      {/* Confirmation card (legacy yes/no) */}
      {isConfirm && run.confirmationPrompt && (
        <ConfirmationCard
          prompt={run.confirmationPrompt}
          details={run.confirmationDetails}
          onConfirm={onConfirm}
          onDeny={onDeny}
        />
      )}

      {/* Generalised mid-run prompt from requestUserInput - waiting marker only;
          the UserInteractionDrawer above the composer is the answer surface.
          In segments mode the live prompt already renders inline at its position. */}
      {!hasSegments && isConfirm && run.pendingPrompt && (
        <div className="ai-chat-chain" role="list" aria-label="Pending question">
          <PendingPromptRow prompt={run.pendingPrompt} />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="ai-chat-run__error">
          <span className="ai-chat-run__error-msg">
            {/* Guard against raw codes in errorMessage replayed from history
                persisted before the server sanitized run errors. */}
            {run.errorMessage && !looksLikeInternalError(run.errorMessage)
              ? run.errorMessage
              : 'Something went wrong.'}
          </span>
          {run.retryable !== false && (
            <button
              type="button"
              className="ai-chat-btn ai-chat-btn--ghost ai-chat-btn--sm"
              onClick={onRetry}
              aria-label="Retry"
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* RAG references */}
      <RagReferenceList references={run.ragReferences} />
    </div>
  );
}
